import { Component, Inject, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { BehaviorSubject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { BaseModalComponent } from 'common/src/modules/rnpl-common/components';
import { ToasterService } from 'common/src/modules/ui-components/toaster';
import { VatReportsApiService } from 'projects/workspace/src/app/vat-reports/services/vat-reports-api.service';
import { MONTHS_LIST, QUARTERS_LIST } from 'projects/workspace/src/app/analytics/reports/constants';
import { getYearsList } from 'projects/workspace/src/app/shared/helpers';
import { AccountingReportsModel } from 'projects/workspace/src/app/accounting/accounting-settings-module/models';

@Component({
  selector: 'rnpl-create-vat-report-modal',
  templateUrl: './create-vat-report-modal.component.html'
})
export class CreateVatReportModalComponent extends BaseModalComponent implements OnDestroy {

  public form: FormGroup;
  public yearsList: string[] = getYearsList(2025);
  public monthsList: {value: number, label: string}[] = MONTHS_LIST;
  public quartersList: {value: number, label: string}[] = QUARTERS_LIST;

  readonly submitRequest$: BehaviorSubject<boolean> = new BehaviorSubject(null);

  constructor(
    public toasterService: ToasterService,
    private fb: FormBuilder,
    private vatReportsApiService: VatReportsApiService,
    public dialogRef: MatDialogRef<CreateVatReportModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      settings: AccountingReportsModel
    }
  ) {
    super(toasterService);

    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });

    this.initForm();
  }

  private initForm(): void {
    this.form = this.fb.group({
      reportType: ['VAT'],
      declarationType: ['REPORTING'],
      periodType: [this.data.settings.periodType],
      month: [],
      quarter: [],
      year: []
    });

    if (this.data.settings.periodType === 'MONTH') {
      this.monthSelected();
    }
    if (this.data.settings.periodType === 'QUARTER') {
      this.quarterSelected();
    }
  }

  public quarterSelected(): void {
    if (this.quarterControl.value) { return; }

    const currentQuarter = Math.floor((new Date().getMonth() / 3));

    if (currentQuarter === 0) {
      this.quarterControl.patchValue(4);
      this.yearControl.patchValue(new Date().getFullYear() - 1);
    } else {
      this.quarterControl.patchValue(currentQuarter);
      this.yearControl.patchValue(new Date().getFullYear());
    }
  }

  public monthSelected(): void {
    if (this.monthControl.value) { return; }

    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    const reportMonth = currentMonth
      ? currentMonth
      : 12;
    const reportYear = reportMonth === 12
      ? currentYear - 1
      : currentYear;

    this.monthControl.patchValue(reportMonth);
    this.yearControl.patchValue(reportYear);
  }

  public submit(): void {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();

    if (this.form.invalid) { return; }
    if (this.submitRequest$.getValue()) { return; }

    const report = {
      reportType: this.reportTypeControl.value,
      declarationType: this.declarationTypeControl.value,
      periodType: this.periodTypeControl.value,
      year: this.yearControl.value,
      period: this.periodTypeControl.value === 'MONTH'
        ? this.monthControl.value
        : this.quarterControl.value
    };

    this.submitRequest$.next(true);

    this.vatReportsApiService.createReport(report)
      .pipe(
        finalize(() => this.submitRequest$.next(false)),
        takeUntil(this._destroy)
      )
      .subscribe((res) => this.dialogRef.close(res));
  }

  get reportTypeControl(): FormControl { return this.form.get('reportType') as FormControl; }
  get declarationTypeControl(): FormControl { return this.form.get('declarationType') as FormControl; }
  get periodTypeControl(): FormControl { return this.form.get('periodType') as FormControl; }
  get monthControl(): FormControl { return this.form.get('month') as FormControl; }
  get quarterControl(): FormControl { return this.form.get('quarter') as FormControl; }
  get yearControl(): FormControl { return this.form.get('year') as FormControl; }

}
